import React from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import {
  DialogWrapper,
  Divider,
  DialogActions,
  ButtonSpacing,
  ActionButton,
  MarginTop12
} from "./styled";

/**
 * ConfirmDialog component displays a modal dialog for user confirmation.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string|null} [props.title] - Dialog title (set to null to hide)
 * @param {React.ReactNode} props.message - Dialog message/content
 * @param {function} props.handleClose - Handler for closing the dialog
 * @param {boolean} props.isOpen - Whether the dialog is open
 * @param {function} [props.handleConfirm] - Handler for confirm action (if no custom actions)
 * @param {React.ReactNode} [props.actions] - Custom actions to display instead of default buttons
 * @returns {JSX.Element}
 */
export const ConfirmDialog = ({
  title = "Are you sure?",
  message,
  handleClose,
  isOpen,
  handleConfirm,
  actions = null,
  divider = true,
  ...props
}) => {
  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="draggable-dialog-title"
      PaperProps={{
        style: {
          borderRadius: "16px"
        }
      }}
      {...props}
    >
      <DialogWrapper>
        {title !== null && <DialogTitle id="draggable-dialog-title">{title}</DialogTitle>}
        <DialogContent>{message}</DialogContent>
        {divider && <Divider />}
        {actions !== null && <div>{actions}</div>}
        {actions === null && (
          <div className={DialogActions}>
            <div className={ButtonSpacing}>
              <ActionButton onClick={handleClose} color="primary" variant="outlined">
                Cancel
              </ActionButton>
              <ActionButton
                onClick={handleConfirm}
                color="primary"
                variant="contained"
                className={MarginTop12}
              >
                Yes, I{"'"}m sure
              </ActionButton>
            </div>
          </div>
        )}
      </DialogWrapper>
    </Dialog>
  );
};

ConfirmDialog.propTypes = {
  /** Dialog title (set to null to hide) */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Dialog message/content */
  message: PropTypes.node.isRequired,
  /** Handler for closing the dialog */
  handleClose: PropTypes.func.isRequired,
  /** Whether the dialog is open */
  isOpen: PropTypes.bool.isRequired,
  /** Handler for confirm action (if no custom actions) */
  handleConfirm: PropTypes.func,
  /** Custom actions to display instead of default buttons */
  actions: PropTypes.node,
  /** Whether to show divider between content and actions */
  divider: PropTypes.bool
};
